import { useEffect, useState } from 'react';
import { Icon } from '../ui/index.js';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const STORAGE_KEY = 'pv-install-banner';
const DISMISSED = 'dismissed';

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

type Platform = 'ios-safari' | 'android-chrome' | 'desktop' | 'other';

function detectPlatform(): Platform {
  if (typeof window === 'undefined') return 'other';
  const ua = window.navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/.test(ua);
  if (isIos) return 'ios-safari';
  if (/Android/.test(ua)) return 'android-chrome';
  return 'desktop';
}

interface InstructionStep {
  icon: string;
  text: string;
}

function getInstructions(platform: Platform): InstructionStep[] {
  switch (platform) {
    case 'ios-safari':
      return [
        { icon: 'ios_share', text: 'Tocca il pulsante Condividi nella barra di Safari.' },
        { icon: 'add_box', text: 'Scegli “Aggiungi a Home”.' },
        { icon: 'check_circle', text: 'Conferma — l’app si apre come standalone.' },
      ];
    case 'android-chrome':
      return [
        { icon: 'more_vert', text: 'Apri il menu del browser (tre puntini in alto a destra).' },
        { icon: 'install_mobile', text: 'Tocca “Installa app” o “Aggiungi alla schermata Home”.' },
      ];
    default:
      return [
        {
          icon: 'install_desktop',
          text: 'Su Chrome o Edge: clicca l’icona “Installa” nella barra degli indirizzi.',
        },
        { icon: 'menu', text: 'In alternativa: menu del browser → “Installa Photo Vault…”.' },
      ];
  }
}

export function InstallBanner() {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [platform] = useState<Platform>(() => detectPlatform());
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    if (isStandalone()) return true;
    return window.localStorage.getItem(STORAGE_KEY) === DISMISSED;
  });

  useEffect(() => {
    if (dismissed) return;

    const onBeforeInstall = (event: Event) => {
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstallEvent(null);
      setDismissed(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, [dismissed]);

  if (dismissed) return null;
  if (isStandalone()) return null;

  const handleInstall = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === 'accepted') {
      setInstallEvent(null);
    }
  };

  const handleDismiss = () => {
    window.localStorage.setItem(STORAGE_KEY, DISMISSED);
    setDismissed(true);
  };

  const instructions = getInstructions(platform);
  const canPromptInstall = installEvent !== null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="install-banner-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
    >
      <div className="vault-card relative w-full max-w-md rounded-2xl border border-outline p-6 shadow-2xl">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary text-on-primary encryption-glow">
            <Icon name="download" className="text-2xl" />
          </span>
          <div className="flex-1">
            <h2
              id="install-banner-title"
              className="font-display text-lg font-semibold text-on-surface"
            >
              Installa Photo Vault
            </h2>
            <p className="mt-1 text-sm text-on-surface-variant">
              {canPromptInstall
                ? 'Aggiungi l’app alla home per accedere offline e a schermo intero.'
                : 'Per la migliore esperienza installa l’app sul tuo dispositivo.'}
            </p>
          </div>
        </div>

        {!canPromptInstall ? (
          <ol className="mt-5 flex flex-col gap-3 rounded-xl border border-outline bg-surface-container-low p-4">
            {instructions.map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-on-surface">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-outline text-on-surface-variant">
                  <Icon name={step.icon} className="text-base" />
                </span>
                <span className="pt-0.5">{step.text}</span>
              </li>
            ))}
          </ol>
        ) : null}

        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={handleDismiss}
            className="inline-flex h-11 items-center justify-center rounded-lg border border-outline px-4 text-xs font-semibold uppercase tracking-[0.08em] text-on-surface-variant transition-colors hover:text-cyan"
          >
            Non mostrare più
          </button>
          {canPromptInstall ? (
            <button
              type="button"
              onClick={handleInstall}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-primary px-5 text-sm font-semibold text-on-primary encryption-glow transition-all hover:brightness-110 active:scale-[0.98]"
            >
              <Icon name="download" className="text-base" />
              Installa
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
